import type {
  Event,
  FeatureAppDetails,
  Form,
  DigitalLeadsFormLoad,
  DigitalLeadsFormInput,
  DigitalLeadsDataLayerEvent,
  DigitalLeadsFormSubmissionAttempt,
  DigitalLeadsFormSubmissionSuccess,
  DigitalLeadsFormSubmissionError,
  Promotion,
} from '../types/analytics';

import { getOptionsByCompositeCodes } from './vehicleFormattor';

type InitialLoadTrigger = (
  form: Form,
  vehicleDetails: any,
  featureApp: FeatureAppDetails,
  promotion?: Promotion,
  // eslint-disable-next-line
  payload?: any,
) => void;

// extend window interface so we can add a global
declare global {
  interface Window {
    ixisDataLayer: DigitalLeadsDataLayerEvent[];
  }
}

if (typeof window !== 'undefined') {
  window.ixisDataLayer = window.ixisDataLayer || [];
}

// Vehicle context enrichment for modelSelect forms
let _modelLineSelectCustom: string | undefined;

export function setModelLineSelectCustom(value: string | undefined): void {
  _modelLineSelectCustom = value;
}

function getEnrichedVehicle(vehicleDetails: any): any {
  if (_modelLineSelectCustom) {
    return { ...(vehicleDetails || {}), model: { name: _modelLineSelectCustom } };
  }
  return vehicleDetails;
}

// Form context enrichment for multipleSelect forms
let _modelLinesSelected: string[] | undefined;

export function setModelLinesSelected(value: string[] | undefined): void {
  _modelLinesSelected = value;
}

// Handraiser form context enrichment
let _handraiserConfig: { handraiser: string; formTitle: string } | undefined;

export function setHandraiserConfig(
  config: { handraiser: string; formTitle: string } | undefined,
): void {
  _handraiserConfig = config;
}

function getEnrichedForm(form: Form): Form {
  let enriched = form;

  if (_modelLinesSelected && _modelLinesSelected.length > 0) {
    const labels = getOptionsByCompositeCodes(_modelLinesSelected).map((opt) => opt.label);
    enriched = {
      ...enriched,
      formData: {
        ...enriched.formData,
        modelOfInterest: {
          ...enriched.formData.modelOfInterest,
          modelFamily: labels.join(', '),
        },
      },
    };
  }

  if (_handraiserConfig) {
    enriched = {
      ...enriched,
      formType: 'handraiser',
      formName: _handraiserConfig.formTitle,
    };
  }

  return enriched;
}

export const digitalLeadsFormLoad: InitialLoadTrigger = (
  form,
  vehicleDetails,
  featureApp,
  promotion,
) => {
  const enrichedForm = getEnrichedForm(form);
  const event: Event<DigitalLeadsFormLoad> = {
    digitalLeads: {
      type: 'digital_leads_form_load',
      form: enrichedForm,
      vehicle: getEnrichedVehicle(vehicleDetails),
      featureApp,
      promotion,
    },
  };

  if (typeof window !== 'undefined') {
    window.ixisDataLayer.push(event);
  }
};

export const digitalLeadsFormInput: InitialLoadTrigger = (
  form,
  vehicleDetails,
  featureApp,
  promotion,
  input,
) => {
  const enrichedForm = getEnrichedForm(form);
  const event: Event<DigitalLeadsFormInput> = {
    digitalLeads: {
      type: 'digital_leads_form_input',
      form: enrichedForm,
      vehicle: getEnrichedVehicle(vehicleDetails),
      featureApp,
      promotion,
      payload: { input },
    },
  };

  if (typeof window !== 'undefined') {
    window.ixisDataLayer.push(event);
  }
};

export const digitalLeadsFormSubmissionAttempt: InitialLoadTrigger = (
  form,
  vehicleDetails,
  featureApp,
  promotion,
  element,
) => {
  const enrichedForm = getEnrichedForm(form);
  const event: Event<DigitalLeadsFormSubmissionAttempt> = {
    digitalLeads: {
      type: 'digital_leads_form_submission_attempt',
      form: enrichedForm,
      vehicle: getEnrichedVehicle(vehicleDetails),
      featureApp,
      promotion,
      payload: { element },
    },
  };

  if (typeof window !== 'undefined') {
    window.ixisDataLayer.push(event);
  }
};
export const digitalLeadsFormSubmissionSuccess: InitialLoadTrigger = (
  form,
  vehicleDetails,
  featureApp,
  promotion,
) => {
  const enrichedForm = getEnrichedForm(form);
  const event: Event<DigitalLeadsFormSubmissionSuccess> = {
    digitalLeads: {
      type: 'digital_leads_form_submit_success',
      form: enrichedForm,
      promotion,
      vehicle: getEnrichedVehicle(vehicleDetails),
      featureApp,
    },
  };

  if (typeof window !== 'undefined') {
    window.ixisDataLayer.push(event);
  }
};

export const digitalLeadsFormSubmissionError: InitialLoadTrigger = (
  form,
  vehicleDetails,
  featureApp,
  promotion,
  error,
) => {
  const enrichedForm = getEnrichedForm(form);
  const event: Event<DigitalLeadsFormSubmissionError> = {
    digitalLeads: {
      type: 'digital_leads_form_submit_error',
      form: enrichedForm,
      promotion,
      vehicle: getEnrichedVehicle(vehicleDetails),
      featureApp,
      payload: { error },
    },
  };

  if (typeof window !== 'undefined') {
    window.ixisDataLayer.push(event);
  }
};
